<template>
<div class="main menus">

	<div class="list-view no-sub-header">

		<div class="filters">
			<div v-if="getAdministratorFlag && !forAutoPilot">
				<button v-if="!menuId" class="button action" @click="fetchMenu('NEW')">New Menu</button>
			</div>
			<div v-else>
				<div class="title" style="margin: 0 0 1em 0;">Select a menu to edit...</div>
				<div v-for="(menu, index) in menus" :class="[getAdministratorFlag && !forAutoPilot ? 'filter-list-button-small' : 'filter-list-button-large', menuDetail.menu_code == menu.menu_code ? 'active' : '']" @click="fetchMenu(menu.menu_code)">
	                {{ $caplitalizeWords(menu.description) }}
				</div>
			</div>

			<div v-if="autoPilotEnabled" class="label">
				<p>These website menus are part of Auto Pilot and cannot be changed.</p>
				<router-link to="/website/auto-pilot">Manage Auto Pilot...</router-link>
			</div>

			<div v-if="menuId && !autoPilotEnabled">
				<button class="button action spaced" @click="saveMenu()"><span>Save This Menu</span></button>
				<button v-if="getAdministratorFlag" class="button neutral spaced" @click="deleteMenu()"><span>Delete This Menu</span></button>
				<button v-if="getAdministratorFlag" class="button neutral spaced" @click="menuId = ''"><span>Back To List</span></button>
				<div v-if="localSaving" class="message"><i class="fas fa-spinner fa-spin"></i></div>
			</div>

			<div v-if="hasClasses">
				<div class="divider"></div>
				<label>Use the <router-link to="/range-memberships/classes"><b><u>Class Setup</u></b></router-link> program to add a class calendar to your website menus.</label>
			</div>
		</div>

		<loading v-if="localLoading">
			<div>Loading...</div>
		</loading>
		<div v-else-if="!menuId && !forAutoPilot" :class="['main-content', getAdministratorFlag ? 'list-grid' : 'empty-list']" style="grid-gap: 0;">
			<i class="fas fa-arrow-left"></i> Select a menu from the left sidebar to edit.
			<div v-if="getAdministratorFlag" v-for="(menu, index) in menus" class="clickable-cell" style="padding: 0.5em;" @click="fetchMenu(menu.menu_code)">
                {{ menu.description }}
			</div>
		</div>
		<div v-else-if="menuId" class="detail no-sub-header">
			<label v-if="getAdministratorFlag && !forAutoPilot">Menu Description</label>
			<input v-if="getAdministratorFlag && !forAutoPilot" type="text" v-model="menuDetail.description">
            <h1 v-else>{{ menuDetail.description }}</h1>
			<div class="add-remove">
				<div>
					<div class="note">An available <b>menu item</b> is a link that can be added to a menu. Create new menu items in this column before adding them to the {{ localFilters.menu.menu_label }} menu.</div>
					<div class="heading">Available menu items</div>
					<div v-if="!autoPilotEnabled" class="add-remove-wrapper">
						<div class="four-one-columns">
							<input type="text" placeholder="Filter available menu items..." v-model="menuItemsAvailableFilterText">
							<div>
								<button class="button action" style="float: right; margin: 0.15em 0 0 0;" @click="editMenuItem('new')"><span>New</span></button>
							</div>
						</div>
						<table class="items-available">
							<tr v-for="(available, index) in filteredMenuItemsAvailable">
								<td @click="editMenuItem(available)">{{ available.menu_label }}</td>
								<td @click="editMenuItem(available)">{{ available.link_value }}</td>
								<td><button class="action" @click="addMenuItem(available.menu_item_id)"><i class="fas fa-chevron-right"></i></button></td>
							</tr>
						</table>
					</div>
				</div>
				<div>
					<div class="note">A <b>menu</b> is a collection of menu items. Click the blue arrow next to a menu item to add it to this {{ localFilters.menu.menu_label }} menu, or click the red X to remove a menu item from this menu.</div>
					<div class="heading">Menu</div>
					<div v-if="menuDetail.menu_code == 'PRODUCT_NAV'" style="margin: 0.5em 0 0 0;">
						<input type="checkbox" v-model="menuDetail.default_menu" /> Set Products Menu to Default Item Links?
						<i class="fas fa-question-circle" @click="helperModal=true"></i>
					</div>
					<div v-if="menuDetail.menu_code == 'PRODUCT_NAV'" style="margin: 0.5em 0 0 0;">
						Include a Browse menu item in your product menu?
	                    <label class="radio first" style="font-size: 0.8em;">
	                        <input type="radio" v-model="menuDetail.browse_menu" value="1" :disabled="autoPilotEnabled">
	                        <span :class="{ disabled: autoPilotEnabled }"><i class="fas fa-check-circle"></i>Yes</span>
	                    </label>
	                    <label class="radio last" style="font-size: 0.8em;">
	                        <input type="radio" v-model="menuDetail.browse_menu" value="" :disabled="autoPilotEnabled">
	                        <span :class="{ disabled: autoPilotEnabled }"><i class="fas fa-check-circle"></i>No</span>
	                    </label>
					</div>
					<div class="add-remove-wrapper">
						<table class="items-selected">
							<draggable class="menu-items" handle=".drag" :sort="true" v-model="localMenuItems" @end="endDrag" group="menu_items" tag="tbody">
								<tr class="draggable" v-for="(menu_item, index) in localMenuItems">
									<td><button :class="autoPilotEnabled ? 'disabled' : 'delete'" @click="removeMenuItem(menu_item.menu_item_id)"><i class="fas fa-times link pointer"></i></button></td>
									<td @click="editMenuItem(menu_item)">{{ menu_item.menu_label }}</td>
									<td @click="editMenuItem(menu_item)">{{ menu_item.link_value }}</td>
									<td v-if="!autoPilotEnabled" class="drag right"><i class="fas fa-grip-vertical"></i></td>
								</tr>
							</draggable>
						</table>
					</div>
				</div>
			</div>
		</div>
	</div>

    <message-modal v-if="message" :message="message" :loading="saving" :errorStatus="messageStatus" :timeout="messageTimeout" @dismiss="message = ''" />

	<div v-if="helperModal" class="modal">
		<div class="inner">
			<h2>Set Products Menu to Default Item Links</h2>
			<div class="content">
				<p><strong>Checking this box</strong> and clicking the <strong>Save This Menu</strong> button in the left sidebar will set the following Menu Item Links for your Products Menu:</p>
				<ul style="padding-left:2em;"><li>Handguns</li><li>Rifles</li><li>Shotguns</li><li>Ammunition</li><li>Optics</li><li>Accessories</li></ul><br/>
				<p>This will overwrite any Menu Items you already have set for your Products Menu.</p>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="helperModal=false">Got It!</div>
			</div>
		</div>
	</div>

	<div v-if="showModal" class="modal">
		<div class="inner">
			<h2>{{ editingMenuItem.menu_label ? 'Edit' : 'New' }} Menu Item</h2>
			<div class="content">
				<label>Menu Item Label</label>
				<input type="text" v-model="editingMenuItem.menu_label">
                <template v-if="showLinkType">
                    <label>Link Type</label>
    				<select v-model="editingMenuItem.link_type" @change="getLinksToList()">
    					<option value=''>Select...</option>
    					<option v-for="(linkType, linkTypeCode) in linkTypes" :value="linkTypeCode">{{ linkType }}</option>
    				</select>
                </template>
				<label v-if="!['HOME','RANGE_CALENDAR'].includes(editingMenuItem.link_type)">Links To</label>
                <input v-if="['EXTERNAL','ROUTE','LEGACY'].includes(editingMenuItem.link_type)" type="text" v-model="editingMenuItem.link_value" @keyup.enter="saveMenuItem()">
				<select v-else-if="editingMenuItem.link_type == 'SUB_MENU'" v-model="editingMenuItem.sub_menu_id">
					<option value=''>Select...</option>
					<option v-for="(menu, key) in menus" :value="menu.menu_id">{{ menu.description }}</option>
				</select>
				<div v-else-if="linksToList && linksToList.length == 0 && editingMenuItem.link_type == 'MANUFACTURER'">
					<div class="input-with-control">
						<input type="text" v-model="editingMenuItem.link_value" placeholder="Search manufacturers..." @keyup.enter="searchManufacturers()">
						<div class="button action input-go" @click="searchManufacturers()">
							<i :class="searchLoading ? 'fas fa-spinner fa-spin' : 'fas fa-search'"></i>
						</div>
					</div>
				</div>
				<div v-else-if="linksToList && linksToList.length == 0 && editingMenuItem.link_type == 'PRODUCT'">
					<div class="input-with-control">
						<input type="text" v-model="editingMenuItem.link_value" placeholder="Search products..." @keyup.enter="searchProducts()">
						<div class="button action input-go" @click="searchProducts()">
							<i :class="searchLoading ? 'fas fa-spinner fa-spin' : 'fas fa-search'"></i>
						</div>
					</div>
				</div>
				<select v-else-if="!['HOME','RANGE_CALENDAR'].includes(editingMenuItem.link_type)" v-model="editingMenuItem.link_value">
					<option value=''>Select...</option>
					<option v-for="(link, key) in linksToList" :value="link.link_value">{{ link.description }}</option>
				</select>
				<div v-if="editingMenuItem.menu_item_id" class="checkbox-group">
					<span :class="['pointer', 'far', editingMenuItem.deactivate ? 'fa-check-square' : 'fa-square']" @click="editingMenuItem.deactivate = !editingMenuItem.deactivate"></span>
					<label>Deactive this menu item</label>
				</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="cancelMenuItem()">Cancel</div>
				<div class="button neutral spaced" @click="saveMenuItem('deactivate')"><span>Delete</span></div>
				<div class="button action spaced" @click="saveMenuItem()"><span>Save Menu Item</span></div>
                <div v-if="localSaving" class="message"><i class="fas fa-spinner fa-spin"></i></div>
				<div v-if="localEditingMenuItemMessage" class="message">{{ localEditingMenuItemMessage }}</div>
			</div>
		</div>
	</div>

</div>
</template>

<script>
import { mapGetters } from 'vuex'
export default {
	name: 'menus',
    props: [ 'menuCode', 'showLinkType' ],
	data() {
		return {
            localLoading: false,
            localFilters: {
                page: null,
				search_term: '',
				menu: {
                    client_id: '',
                    menu_id: '',
                    menu_label: '',
                    link_type: '',
                    link_value: '',
                    sub_menu_id: '',
                    sort_order: '',
                    menu_code: '',
                    description: '',
                    menu_items_available: '',
                    menu_items: []
                }
            },
            menus: [],
            localMenuItems: [],
			hasClasses: false,
			linkTypes: {},
			paging: {},
			menuId: '',
			menuDetail: {},
			showModal: false,
			helperModal: false,
			menuItemsAvailable: [],
			menuItemsAvailableFilterText: '',
			editingMenuItem: {
				link_value: ''
			},
            editingMenuItemMessage: '',
            localEditingMenuItemMessage: '',
			linksToList: [],
			searchLoading: false,
			message: '',
			messageTimeout: 0,
			messageStatus: '',
			clientId: '',
			localSaving: false,
			forAutoPilot: false,
			autoPilotEnabled: false
		}
	},
	created() {
		this.forAutoPilot = this.$route.query.for_auto_pilot
		if (!this.getAdministratorFlag) {
			this.getAutoPilotEnabled()
		}
        this.fetchMenus()
		this.getLinkTypes()
	},
    watch: {
        editingMenuItemMessage(newVal, oldVal) {
            this.localEditingMenuItemMessage = newVal
        },
        doCancelMenuItem(newVal, oldVal) {
            this.cancelMenuItem()
        }
    },
	computed: {
        ...mapGetters([
            'getAdministratorFlag'
        ]),
		menuItemsOptions() {
			return {
				group: { name: 'menu_items' },
				draggable: '.draggable',
				ghostClass: "sortable-ghost",
				chosenClass: "sortable-chosen",
				dragClass: "sortable-drag",
				handle: ".drag"
			}
		},
		filteredMenuItemsAvailable() {
			var filteredMenuItemsAvailable = this.menuItemsAvailable.filter(item => {
                if (!item.menu_label) return false
				return item.menu_label.toLowerCase().indexOf(this.menuItemsAvailableFilterText.toLowerCase()) > -1
			})
			return filteredMenuItemsAvailable
		},
	},
	methods: {
		getAutoPilotEnabled() {
			this.apiCall({ destination: 'get_item_preference', data: { preference_code: 'ENABLE_AUTO_PILOT' } }).then(obj => {
				this.autoPilotEnabled = obj.ENABLE_AUTO_PILOT != ''
			}).catch(err => {
				console.log(err)
			})
		},
        fetchMenus() {
            this.localLoading = true
            var data = {}
            if (this.menuCode) {
                data.menu_code = this.menuCode
            }
            this.apiCall({ destination: 'get_list_menus', data: data }).then(obj => {
                this.menus = obj.menus
				if (obj.has_classes) {
					this.hasClasses = true
				}
                this.localLoading = false
            }).catch(err => {
                this.localLoading = false
                console.log(err);
            })
        },
        fetchMenu(menuCode) {
			this.localLoading = true
			this.menuItemsFilterText = ''
			this.menuItemsAvailableFilterText = ''
			this.apiCall({ destination: 'get_item_menu', data: { menu_code: menuCode, client_id: this.getClientId }} ).then(obj => {
				this.localLoading = false
				this.menuId = obj.menu.menu_id
				this.menuDetail = obj.menu
				this.menuItemsAvailable = obj.menu_items_available
				this.localMenuItems = obj.menu_items
			}).catch(err => {
				console.log(err)
			})
		},
        saveMenu(menu) {
            this.saving = true
			this.messageStatus = ''
			this.messageTimeout = 0
			let postData = {
				menu_id: this.menuDetail.menu_id,
				menu_code: this.menuDetail.menu_code,
				description: this.menuDetail.description,
				menu_items: this.localMenuItems
			}
			if (this.menuDetail.menu_code == 'PRODUCT_NAV') {
				postData.browse_menu = this.menuDetail.browse_menu
				postData.default_menu = this.menuDetail.default_menu
			}
			this.apiCall({ destination: 'save_item_menu', data: postData }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'error') {
					this.message = obj.message
				} else {
                    this.message = 'Menu saved!'
					this.messageTimeout = 3000
					this.fetchMenu(obj.menu_code)
				}
			}).catch(err => {
				console.log(err)
			})
        },
        deleteMenu(menuId) {
            this.apiCall({ destination: 'delete_item_menu', data: { menu_id: menuId }} ).then(obj => {
				this.localLoading = false
				if (obj.status == 'error') {
					this.message = obj.message
				} else {
                    this.fetchMenus()
				}
			}).catch(err => {
				console.log(err)
			})
        },
        saveMenuItem(action) {
            this.localEditingMenuItemMessage = ''
            this.editingMenuItem.link_value = (this.editingMenuItem.link_value == '') ? this.editingMenuItem.menu_label.dasherize() : this.editingMenuItem.link_value
            this.editingMenuItem.link_value = ['HOME', 'RANGE_CALENDAR'].includes(this.editingMenuItem.link_type) ? '' : this.editingMenuItem.link_value
			this.localEditingMenuItemMessage = ''
			if (!this.editingMenuItem.menu_label) {
				this.localEditingMenuItemMessage = 'Menu Item Label is required.'
				return false
			}
			if (this.showLinkType && !['HOME', 'MYACCOUNT', 'RANGE_CALENDAR'].includes(this.editingMenuItem.link_type) && !this.editingMenuItem.link_value) {
				this.localEditingMenuItemMessage = 'Links To is required.'
				return false
			}
			if (action == 'deactivate') {
				this.editingMenuItem.deactivate = true
			}
            this.saving = true
			this.messageStatus = ''
			this.messageTimeout = 0
            this.apiCall({ destination: 'save_item_menu_item', data: { menu_item: this.editingMenuItem }} ).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'error') {
					this.localEditingMenuItemMessage = obj.message
				} else {
					this.fetchMenu(this.menuDetail.menu_code)
                    // Sort of hackish, just flip-flopping this.doCancelMenuItem to let
                    // the menu component know to fire the function cancelMenuItem
					// this.doCancelMenuItem = !this.doCancelMenuItem
                    this.cancelMenuItem()
                    this.message = "Menu Item " + (action == 'deactivate' ? 'deleted!' : 'saved!')
					this.messageTimeout = 3000
				}
			}).catch(err => {
                this.localSaving = false
				console.log(err)
			})
            // this.$emit('saveMenuItem', this.editingMenuItem)
			if (this.editingMenuItem.deactivate) {
				for (var index = 0; index < this.menuItemsAvailable.length; index++) {
					if (this.menuItemsAvailable[index].menu_item_id == this.editingMenuItem.menu_item_id) {
						this.menuItemsAvailable.splice(index, 1);
					}
				}
			}
		},
		getLinkTypes() {
			this.apiCall({ destination: 'get_list_menu_link_types' }).then(obj => {
				this.linkTypes = obj.menu_link_types
			}).catch(err => {
				console.log(err)
			})
		},
		endDrag(event) {
			var menuItems = []
			menuItems = this.localMenuItems.map((menuItem, index) => {
				menuItem.sort_order = index + 1
				return menuItem
			})
			this.localMenuItems = menuItems
		},
		addMenuItem(menuItemId) {
			for (var index = 0; index < this.menuItemsAvailable.length; index++) {
				if (this.menuItemsAvailable[index].menu_item_id == menuItemId) {
					this.localMenuItems.push(this.menuItemsAvailable[index])
					this.menuItemsAvailable.splice(index, 1);
				}
			}
		},
		removeMenuItem(menuItemId) {
			if (this.autoPilotEnabled) { return }
			for (var index = 0; index < this.localMenuItems.length; index++) {
				if (this.localMenuItems[index].menu_item_id == menuItemId) {
					this.menuItemsAvailable.push(this.localMenuItems[index])
					this.localMenuItems.splice(index, 1);
				}
			}
		},
		editMenuItem(menuItem) {
			if (this.autoPilotEnabled) { return }
			if (menuItem == 'new') {
				this.editingMenuItem = {}
			} else {
				this.editingMenuItem = menuItem
			}
			this.editingMenuItem.link_value = this.editingMenuItem.link_value || ''
			this.getLinksToList()
			this.editingMenuItem.deactivate = false
			this.showModal = true
		},
		getLinksToList() {
			if (!this.editingMenuItem.link_type) {
				return false
			}
			this.linksToList = []
			if (['EXTERNAL', 'PRODUCT', 'MANUFACTURER', 'ROUTE', 'RANGE_CALENDAR'].includes(this.editingMenuItem.link_type)) {
				return false
			}
			this.apiCall({ destination: 'get_list_links_to', data: { link_type: this.editingMenuItem.link_type }} ).then(obj => {
				this.localLoading = false
				if (obj.status == 'error') {
					this.message = obj.message
				} else {
					this.linksToList = obj.links_to
				}
			}).catch(err => {
				console.log(err)
			})
		},
		searchManufacturers() {
			this.searchLoading = true
			this.apiCall({ destination: 'get_list_links_to', data: { link_type: this.editingMenuItem.link_type, search_term: this.editingMenuItem.link_value }} ).then(obj => {
				this.searchLoading = false
				if (obj.status == 'error') {
					this.message = obj.message
				} else {
					// set the linksToList
					this.linksToList = obj.links_to
					// set the link_value to the first item in the linksToList
					if (this.linksToList.length > 0) {
						this.editingMenuItem.link_value = this.linksToList[0]['link_name']
					}
				}
			}).catch(err => {
				console.log(err)
			})
		},
		searchProducts() {
			this.searchLoading = true
			this.apiCall({ destination: 'get_list_links_to', data: { link_type: this.editingMenuItem.link_type, search_term: this.editingMenuItem.link_value }} ).then(obj => {
				this.searchLoading = false
				if (obj.status == 'error') {
					this.message = obj.message
				} else {
					// set the linksToList
					this.linksToList = obj.links_to
					// set the link_value to the first item in the linksToList
					if (this.linksToList.length > 0) {
						this.editingMenuItem.link_value = this.linksToList[0]['link_value']
					}
				}
			}).catch(err => {
				console.log(err)
			})
		},
		cancelMenuItem() {
			this.editingMenuItem = {}
			this.showModal = false
		}
	}
}
</script>
